import { useEffect, useState } from "react";
import { MapPin, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = { name: "", level: "Kabupaten/Kota", regency: "", address: "", phone: "", email: "", website: "", chief: "" };

export default function OfficesAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [delItem, setDelItem] = useState(null);

  const load = () => api.get("/offices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (o) => {
    setForm({ name: o.name, level: o.level, regency: o.regency || "", address: o.address || "", phone: o.phone || "", email: o.email || "", website: o.website || "", chief: o.chief || "" });
    setEditId(o.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama kantor wajib diisi"); return; }
    try {
      if (editId) await api.put(`/offices/${editId}`, form);
      else await api.post("/offices", form);
      toast.success(editId ? "Kantor diperbarui" : "Kantor ditambahkan");
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/offices/${delItem.id}`);
      toast.success(`"${delItem.name}" dihapus`);
      setDelItem(null); load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <div>
      <AdminHeader title="Direktori Bawaslu" description="Kelola daftar kantor Bawaslu se-Sulawesi Tenggara — tampil di halaman Bawaslu Terdekat."
        action={<Button onClick={openNew} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="add-office-btn"><Plus className="h-4 w-4 mr-1.5" /> Tambah Kantor</Button>} />

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="offices-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface">
              <TableHead>Nama</TableHead><TableHead>Tingkat</TableHead><TableHead>Alamat</TableHead>
              <TableHead>Telepon</TableHead><TableHead>Email</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((o) => (
              <TableRow key={o.id} data-testid={`office-row-${o.id}`}>
                <TableCell className="font-semibold text-navy">{o.name}</TableCell>
                <TableCell><span className="text-xs font-bold px-2 py-0.5 rounded-full bg-brandred/10 text-brandred">{o.level}</span></TableCell>
                <TableCell className="text-sm text-slate-500 max-w-xs"><span className="line-clamp-1">{o.address}</span></TableCell>
                <TableCell className="text-sm text-slate-500">{o.phone}</TableCell>
                <TableCell className="text-sm text-slate-500">{o.email}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(o)} data-testid={`edit-office-${o.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-brandred hover:text-brandred" onClick={() => setDelItem(o)} data-testid={`delete-office-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Belum ada kantor.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Tambah"} Kantor Bawaslu</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Kantor *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="office-name" placeholder="Bawaslu Kabupaten ..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tingkat</Label>
                <Select value={form.level} onValueChange={(v) => set("level", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="office-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Provinsi">Provinsi</SelectItem>
                    <SelectItem value="Kabupaten/Kota">Kabupaten/Kota</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Kabupaten/Kota</Label><Input value={form.regency} onChange={(e) => set("regency", e.target.value)} className="mt-1.5" data-testid="office-regency" placeholder="Kabupaten ..." /></div>
            </div>
            <div><Label>Alamat</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5" data-testid="office-address" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Telepon</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" data-testid="office-phone" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="office-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Website</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} className="mt-1.5" placeholder="contoh.bawaslu.go.id" /></div>
              <div><Label>Penanggung Jawab</Label><Input value={form.chief} onChange={(e) => set("chief", e.target.value)} className="mt-1.5" placeholder="Koordinator Divisi ..." /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="save-office">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus kantor ini?</AlertDialogTitle>
            <AlertDialogDescription>"{delItem?.name}" akan dihapus permanen dari direktori publik.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-brandred hover:bg-brandred-700" data-testid="confirm-delete-office">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
